import React, { useEffect, useCallback } from 'react';
import {
  HiEye,
  HiArrowDownTray,
  HiDocumentText,
  HiFolder,
  HiArrowsPointingOut,
} from 'react-icons/hi2';
import { HiX } from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';

export default function PreviewModal({ open, doc, onClose, formatFileSize }) {
  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'unset';
      };
    }
  }, [open, handleKeyDown]);
  // Pretty-print MIME types into human-friendly labels
  const getPrettyType = raw => {
    if (!raw) return 'Unknown';
    const value = String(raw).toLowerCase();
    if (value === 'application/pdf') return 'PDF';
    if (value === 'application/msword') return 'Word (DOC)';
    if (value === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
      return 'Word (DOCX)';
    if (value === 'application/vnd.ms-excel') return 'Excel (XLS)';
    if (value === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      return 'Excel (XLSX)';
    if (value.startsWith('image/')) {
      const subtype = value.split('/')[1] || 'image';
      return `Image (${subtype.toUpperCase()})`;
    }
    const parts = value.split('/');
    const tail = parts[1] || parts[0] || value;
    return tail.replace(/\./g, ' ').toUpperCase();
  };

  const sizeLabel = formatFileSize(doc?.fileSize);
  const typeLabel = getPrettyType(doc?.fileType);
  const categoryLabel = doc?.category ? doc.category : 'Uncategorized';

  return (
    <AnimatePresence>
      {open && doc && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
            className="relative w-full max-w-6xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
          >
            {/* HEADER */}
            <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-green-50 to-white px-6 py-5">
              <div className="flex items-center gap-4">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.1 }}
                  className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-600 text-white shadow-lg shadow-green-500/40"
                >
                  <HiEye className="h-6 w-6" />
                </motion.div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-bold text-black">Document Preview</h3>
                  <p className="mt-1 truncate text-sm font-semibold text-gray-600">
                    {doc?.fileName}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open(doc.url, '_blank')}
                  className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 shadow-sm transition-all hover:bg-gray-50"
                  title="Open in new tab"
                >
                  <HiArrowsPointingOut className="h-5 w-5" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="rounded-lg border border-gray-200 bg-white p-2 text-gray-500 shadow-sm transition-all hover:bg-gray-50 hover:text-gray-700"
                  title="Close (Esc)"
                >
                  <HiX className="h-6 w-6" />
                </motion.button>
              </div>
            </div>

            {/* BODY */}
            <div className="max-h-[80vh] overflow-auto bg-gray-50 p-6">
              {doc?.fileType?.startsWith('image/') ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="flex justify-center"
                >
                  <img
                    src={doc.url}
                    alt={doc.fileName}
                    className="max-h-[70vh] max-w-full rounded-lg shadow-lg ring-1 ring-black/5"
                  />
                </motion.div>
              ) : doc?.fileType === 'application/pdf' ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="h-[75vh] w-full overflow-hidden rounded-lg border border-gray-200 shadow-inner"
                >
                  <iframe src={doc.url} className="h-full w-full" title={doc.fileName} />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex h-[70vh] w-full items-center justify-center rounded-lg border border-gray-200 bg-white"
                >
                  <div className="px-6 text-center">
                    <motion.div
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                    >
                      <HiDocumentText className="mx-auto mb-4 h-16 w-16 text-gray-400" />
                    </motion.div>
                    <h3 className="text-lg font-medium text-black">Preview Not Available</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      This file type cannot be previewed in the browser.
                    </p>
                    <div className="mt-6 flex justify-center gap-3">
                      <motion.a
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        href={doc.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:bg-blue-700"
                      >
                        <HiArrowDownTray className="h-4 w-4" />
                        Download to View
                      </motion.a>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onClose}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-black shadow-sm transition hover:bg-gray-50"
                      >
                        Close
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* FOOTER */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm ring-1 ring-black/5">
                    <span className="text-gray-500">Size:</span>
                    <span className="font-semibold text-black">{sizeLabel}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium shadow-sm ring-1 ring-black/5">
                    <HiDocumentText className="h-3.5 w-3.5 text-blue-600" />
                    <span className="font-semibold text-black">{typeLabel}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-medium shadow-sm ring-1 ring-black/5">
                    <HiFolder className="h-3.5 w-3.5 text-orange-600" />
                    <span className="font-semibold text-black">{categoryLabel}</span>
                  </span>
                </div>

                <div className="flex justify-end gap-3">
                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href={doc?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all hover:bg-blue-700"
                    title="Download"
                  >
                    <HiArrowDownTray className="h-4 w-4" />
                    Download
                  </motion.a>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onClose}
                    className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-black shadow-sm transition-all hover:bg-gray-50"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
